import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, Row, Col, Input, Form, Select, InputNumber, notification, Radio, Spin } from "antd";
import { useSelector } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";
import BoxText from "../../../../../components/utils/boxText";
import ButtonStandard from "../../../../../components/utils/button";
import { MapCenterContainer } from "../styles";
import {
  getFarmMapParameters,
  createFarmMapParameter,
  updateFarmMapParameter
} from "../../../../../services/farmMapParameterService";
import { logEventError, logEventInfo } from "../../../../../utils/logger";

const { Option } = Select;

const MapCenterCard = ({ onLocationChange, translation, savedLocation, setSavedLocation }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlacesLoaded, setIsPlacesLoaded] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [parameterId, setParameterId] = useState(null);
  const [isLoadingParameter, setIsLoadingParameter] = useState(false);

  const geocoderRef = useRef(null);

  const {
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  // Schema de validação
  const validationSchema = Yup.object({
    locationType: Yup.string().required(translation.areaMap.centerCard.validation.locationTypeRequired),
    address: Yup.string().when('locationType', {
      is: 'address',
      then: Yup.string().required(translation.areaMap.centerCard.validation.addressRequired),
      otherwise: Yup.string().nullable()
    }),
    latitude: Yup.number().when('locationType', {
      is: 'coordinates',
      then: Yup.number()
        .required(translation.areaMap.centerCard.validation.latitudeRequired)
        .min(-90, translation.areaMap.centerCard.validation.latitudeMin)
        .max(90, translation.areaMap.centerCard.validation.latitudeMax),
      otherwise: Yup.number().nullable()
    }),
    longitude: Yup.number().when('locationType', {
      is: 'coordinates',
      then: Yup.number()
        .required(translation.areaMap.centerCard.validation.longitudeRequired)
        .min(-180, translation.areaMap.centerCard.validation.longitudeMin)
        .max(180, translation.areaMap.centerCard.validation.longitudeMax),
      otherwise: Yup.number().nullable()
    })
  });

  const initialValues = {
    locationType: 'coordinates',
    address: '',
    latitude: -15.7801,
    longitude: -47.9292,
    description: ''
  };

  // Verificar se Google Maps API está disponível
  const checkMapsAPI = useCallback(() => {
    logEventInfo('Verificando Google Maps API...', {
      google: !!window.google,
      maps: !!(window.google && window.google.maps)
    });

    if (window.google && window.google.maps) {
      logEventInfo('Google Maps API carregada com sucesso!');
      setIsPlacesLoaded(true);
    } else {
      logEventInfo('Google Maps API ainda não carregada, tentando novamente...');
      // Aguardar um pouco e tentar novamente
      setTimeout(checkMapsAPI, 500);
    }
  }, []);

  // Buscar endereços por texto
  const searchAddresses = useCallback(async (searchText) => {
    if (!isPlacesLoaded || !window.google || !searchText.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    try {
      setIsSearching(true);

      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }

      return new Promise((resolve, reject) => {
        geocoderRef.current.geocode({
          address: searchText,
          componentRestrictions: { country: 'BR' }
        }, (results, status) => {
          if (status === 'OK' && results.length > 0) {
            const formattedResults = results.map((result, index) => ({
              id: index,
              formatted_address: result.formatted_address,
              latitude: result.geometry.location.lat(),
              longitude: result.geometry.location.lng(),
              place_id: result.place_id,
              types: result.types
            }));

            setSearchResults(formattedResults);
            setShowResults(true);
            resolve(formattedResults);
          } else {
            setSearchResults([]);
            setShowResults(false);
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });
    } catch (error) {
      logEventError('Erro na busca de endereços:', error);
      setSearchResults([]);
      setShowResults(false);
      throw error;
    } finally {
      setIsSearching(false);
    }
  }, [isPlacesLoaded]);

  // Selecionar endereço dos resultados
  const handleAddressSelection = useCallback((addressData, setFieldValue) => {
    setSelectedAddress(addressData);
    setShowResults(false);

    if (setFieldValue) {
      setFieldValue('address', addressData.formatted_address);
      setFieldValue('latitude', addressData.latitude);
      setFieldValue('longitude', addressData.longitude);
    }
  }, []);

  // Limpar resultados da busca
  const clearSearchResults = useCallback(() => {
    setSearchResults([]);
    setShowResults(false);
    setSelectedAddress(null);
  }, []);

  // Salvar dados
  const handleLocationFormSubmit = useCallback(async (values) => {
    try {
      setIsLoading(true);

      const parameterData = {
        locationType: values.locationType,
        address: values.address || null,
        latitude: values.latitude,
        longitude: values.longitude,
      };

      let response;

      if (parameterId) {
        // Atualizar parâmetro existente
        response = await updateFarmMapParameter(groupId, farmId, parameterId, parameterData);
      } else {
        // Criar novo parâmetro
        response = await createFarmMapParameter(groupId, farmId, parameterData);
        setParameterId(response.data.id);
      }

      const locationData = {
        ...values,
        savedAt: new Date().toISOString()
      };

      // Salvar no estado
      setSavedLocation(locationData);
      setIsEditing(false);
      clearSearchResults();

      // Aplicar a localização ao mapa e recarregar
      if (onLocationChange) {
        onLocationChange(locationData);
      }

      notification.success({
        message: translation.areaMap.centerCard.messages.saved,
        description: translation.areaMap.centerCard.messages.savedDescription,
      });
    } catch (error) {
      logEventError('Erro ao salvar localização:', error);
      notification.error({
        message: translation.areaMap.centerCard.messages.saveError,
        description: translation.areaMap.centerCard.messages.saveErrorDescription,
      });
    } finally {
      setIsLoading(false);
    }
  }, [groupId, farmId, parameterId, setSavedLocation, onLocationChange, translation, clearSearchResults]);

  // Verificar se Google Maps API está disponível ao montar o componente
  useEffect(() => {
    checkMapsAPI();
  }, [checkMapsAPI]);

  // Carregar parâmetro existente ao montar o componente
  useEffect(() => {
    let isMounted = true;
    async function loadExistingParameter() {
      if (!groupId || !farmId) return;

      logEventInfo('Carregando parâmetro existente...');

      try {
        setIsLoadingParameter(true);

        // Buscar todos os parâmetros do mapa da fazenda
        const response = await getFarmMapParameters(groupId, farmId);

        if (response && response.data) {
          // Procurar por um parâmetro com tipo 'map_center'
          const mapCenterParameter = response.data.results;

          if (mapCenterParameter) {
            setParameterId(mapCenterParameter.id);
            const locationData = {
              locationType: mapCenterParameter.locationType || 'coordinates',
              address: mapCenterParameter.address || '',
              latitude: mapCenterParameter.latitude || -15.7801,
              longitude: mapCenterParameter.longitude || -47.9292,
              description: mapCenterParameter.description || '',
              savedAt: mapCenterParameter.updatedAt || mapCenterParameter.createdAt
            };

            setSavedLocation(locationData);

            // Aplicar as coordenadas ao mapa imediatamente
            onLocationChange({
              ...locationData,
              latitude: mapCenterParameter.latitude || -15.7801,
              longitude: mapCenterParameter.longitude || -47.9292,
              savedAt: mapCenterParameter.updatedAt || mapCenterParameter.createdAt
            });
          } else {
            // Não encontrou parâmetro do tipo map_center, será criado um novo
            logEventInfo('Nenhum parâmetro do tipo map_center encontrado, será criado um novo');
            setParameterId(null);
            // Notificar que os parâmetros foram carregados (mesmo que vazios)
            onLocationChange(null);
          }
        } else {
          // Não há parâmetros, será criado um novo
          logEventInfo('Nenhum parâmetro encontrado, será criado um novo');
          setParameterId(null);
          // Notificar que os parâmetros foram carregados (mesmo que vazios)
          onLocationChange(null);
        }
      } catch (error) {
        // Se houver erro na busca, será criado um novo parâmetro
        logEventInfo('Erro ao buscar parâmetros, será criado um novo:', error.message);
        setParameterId(null);
        // Notificar que os parâmetros foram carregados (mesmo que com erro)
        onLocationChange(null);
      } finally {
        setIsLoadingParameter(false);
        isMounted = false;
      }
    }
    if(isMounted) {
      loadExistingParameter();
    }
    return () => {
      isMounted = false;
    }
  }, [groupId, farmId, onLocationChange, setSavedLocation]);

  // Limpar resultados quando sair do modo de edição
  useEffect(() => {
    if (!isEditing) {
      clearSearchResults();
    }
  }, [isEditing, clearSearchResults]);


  // Se está carregando parâmetros, mostrar loading
  if (isLoadingParameter) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#666' }}>
            {translation.areaMap.centerCard.loading || 'Carregando configurações...'}
          </p>
        </div>
      </Card>
    );
  }

  // Se não há dados salvos, mostrar card de informação com formulário
  if (!savedLocation && !isEditing) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <MapCenterContainer>
          <BoxText
            color="#1890ff"
            iconComponent={<i className="fas fa-map-marker-alt" style={{ fontSize: '24px' }} />}
            alingCenter={true}
          >
            {translation.areaMap.centerCard.infoMessage}
          </BoxText>

          <div style={{ marginTop: 16 }}>
            <ButtonStandard
              buttonType="principal"
              onClick={() => setIsEditing(true)}
              style={{ width: '100%' }}
            >
              {translation.areaMap.centerCard.configureLocation}
            </ButtonStandard>
          </div>
        </MapCenterContainer>
      </Card>
    );
  }

  // Se está editando ou não há dados salvos, mostrar formulário
  if (isEditing || !savedLocation) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <MapCenterContainer>
          <h4>{translation.areaMap.centerCard.formTitle}</h4>

          <Formik
            initialValues={savedLocation || initialValues}
            validationSchema={validationSchema}
            onSubmit={handleLocationFormSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, isSubmitting }) => (
              <Form layout="vertical">
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      label={translation.areaMap.centerCard.form.locationType}
                      validateStatus={errors.locationType && touched.locationType ? 'error' : ''}
                      help={errors.locationType && touched.locationType ? errors.locationType : ''}
                    >
                      <Select
                        value={values.locationType}
                        onChange={(value) => setFieldValue('locationType', value)}
                        style={{ width: '100%' }}
                      >
                        <Option value="coordinates">{translation.areaMap.centerCard.form.coordinates}</Option>
                        <Option value="address">{translation.areaMap.centerCard.form.address}</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  {values.locationType === 'address' && (
                    <Col span={24}>
                      <Form.Item
                        label={translation.areaMap.centerCard.form.address}
                        validateStatus={errors.address && touched.address ? 'error' : ''}
                        help={errors.address && touched.address ? errors.address : ''}
                      >
                        <div style={{ position: 'relative' }}>
                          <Input
                            name="address"
                            value={values.address}
                            onChange={(e) => {
                              handleChange(e);
                              if (e.target.value.trim()) {
                                searchAddresses(e.target.value);
                              } else {
                                clearSearchResults();
                              }
                            }}
                            onBlur={handleBlur}
                            placeholder={translation.areaMap.centerCard.form.addressPlaceholder}
                            disabled={!isPlacesLoaded}
                            autoComplete="off"
                            style={{
                              backgroundColor: isPlacesLoaded ? 'white' : '#f5f5f5',
                              cursor: isPlacesLoaded ? 'text' : 'not-allowed'
                            }}
                            suffix={isSearching ? <Spin size="small" /> : null}
                          />

                          {/* Resultados da busca */}
                          {showResults && searchResults.length > 0 && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #d9d9d9',
                              borderRadius: '6px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              zIndex: 1000,
                              maxHeight: '200px',
                              overflowY: 'auto'
                            }}>
                              {searchResults.length === 1 ? (
                                <div
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0'
                                  }}
                                  onClick={() => handleAddressSelection(searchResults[0], setFieldValue)}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                >
                                  {searchResults[0].formatted_address}
                                </div>
                              ) : (
                                <Radio.Group
                                  style={{ width: '100%' }}
                                  onChange={(e) => {
                                    const selectedId = e.target.value;
                                    const selectedResult = searchResults.find(result => result.id === selectedId);
                                    if (selectedResult) {
                                      handleAddressSelection(selectedResult, setFieldValue);
                                    }
                                  }}
                                >
                                  {searchResults.map((result) => (
                                    <div key={result.id} style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                                      <Radio value={result.id} style={{ width: '100%' }}>
                                        <span style={{ fontSize: '12px' }}>{result.formatted_address}</span>
                                      </Radio>
                                    </div>
                                  ))}
                                </Radio.Group>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Dados do endereço selecionado */}
                        {selectedAddress && (
                          <div style={{
                            marginTop: '8px',
                            padding: '8px',
                            backgroundColor: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <div><strong>Endereço:</strong> {selectedAddress.formatted_address}</div>
                            <div><strong>Latitude:</strong> {selectedAddress.latitude.toFixed(6)}</div>
                            <div><strong>Longitude:</strong> {selectedAddress.longitude.toFixed(6)}</div>
                          </div>
                        )}

                        {!isPlacesLoaded && (
                          <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                            {translation.areaMap.centerCard.form.loadingPlaces}
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                  )}

                  {values.locationType === 'coordinates' && (
                    <>
                      <Col span={12}>
                        <Form.Item
                          label={translation.areaMap.centerCard.form.latitude}
                          validateStatus={errors.latitude && touched.latitude ? 'error' : ''}
                          help={errors.latitude && touched.latitude ? errors.latitude : ''}
                        >
                          <InputNumber
                            name="latitude"
                            value={values.latitude}
                            onChange={(value) => setFieldValue('latitude', value)}
                            onBlur={handleBlur}
                            placeholder={translation.areaMap.centerCard.form.latitudePlaceholder}
                            min={-90}
                            max={90}
                            step={0.000001}
                            precision={6}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={translation.areaMap.centerCard.form.longitude}
                          validateStatus={errors.longitude && touched.longitude ? 'error' : ''}
                          help={errors.longitude && touched.longitude ? errors.longitude : ''}
                        >
                          <InputNumber
                            name="longitude"
                            value={values.longitude}
                            onChange={(value) => setFieldValue('longitude', value)}
                            onBlur={handleBlur}
                            placeholder={translation.areaMap.centerCard.form.longitudePlaceholder}
                            min={-180}
                            max={180}
                            step={0.000001}
                            precision={6}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <ButtonStandard
                      buttonType="cancel"
                      onClick={() => setIsEditing(false)}
                      style={{ width: '100%' }}
                    >
                      {translation.areaMap.centerCard.form.cancel}
                    </ButtonStandard>
                  </Col>
                  <Col span={12}>
                    <ButtonStandard
                      buttonType="principal"
                      onClick={handleSubmit}
                      loading={isLoading}
                      style={{ width: '100%' }}
                    >
                      {translation.areaMap.centerCard.form.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </MapCenterContainer>
      </Card>
    );
  }

  // Se há dados salvos, mostrar card com informações e botão de editar
  return (
    <Card style={{ marginBottom: 16 }}>
      <MapCenterContainer>
        <Row gutter={16} align="middle">
          <Col span={20}>
            <div>
              <h4 style={{ margin: 0, marginBottom: 8 }}>
                <i className="fas fa-map-marker-alt" style={{ marginRight: 8, color: '#1890ff' }} />
                {translation.areaMap.centerCard.savedLocation}
              </h4>

              {savedLocation.locationType === 'address' ? (
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>{savedLocation.address}</p>
                  {savedLocation.description && (
                    <p style={{ margin: 0, color: '#666', fontSize: '12px' }}>
                      {savedLocation.description}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>
                    {translation.areaMap.centerCard.coordinates}: {savedLocation.latitude.toFixed(6)}, {savedLocation.longitude.toFixed(6)}
                  </p>
                  {savedLocation.description && (
                    <p style={{ margin: 0, color: '#666', fontSize: '12px' }}>
                      {savedLocation.description}
                    </p>
                  )}
                </div>
              )}

              <p style={{ margin: 0, color: '#999', fontSize: '11px' }}>
                {translation.areaMap.centerCard.savedAt}: {new Date(savedLocation.savedAt).toLocaleString()}
              </p>
            </div>
          </Col>
          <Col span={4}>
            <ButtonStandard
              buttonType="secondary"
              onClick={() => setIsEditing(true)}
              style={{ width: '100%' }}
            >
              {translation.areaMap.centerCard.edit}
            </ButtonStandard>
          </Col>
        </Row>
      </MapCenterContainer>
    </Card>
  );
};

export default MapCenterCard;
